import { Component, Input, OnChanges } from '@angular/core';
import { PosTransactionStatusLabels } from 'src/app/models/enums/pos-enums';
import { tryParseJson, unwrapJson } from '../pos-json.helpers';

interface JsonField {
  label: string;
  value: string;
}

interface JsonTable {
  title: string;
  columns: string[];
  rows: string[][];
}

interface JsonSection {
  title?: string;
  fields: JsonField[];
  tables: JsonTable[];
  sections: JsonSection[];
}

/**
 * Kullanıcıya gösterilen alanlar ve Türkçe karşılıkları. Listede olmayan alanlar (sağlayıcının
 * teknik alanları, uygulama/bildirim ayarları, ham durum kodları) gizlenir; ham veride görünür.
 */
const LABELS: Record<string, string> = {
  // Bizim istek modelimiz (PosSaleRequest / PosVoidRequest)
  Reference: 'Referans',
  TerminalLabel: 'Kasa etiketi',
  CurrencyCode: 'Para birimi',
  TotalAmount: 'Toplam tutar',
  Lines: 'Belge satırları',
  Payments: 'Ödemeler',
  Customer: 'Müşteri',
  DocumentNote: 'Belge notu',
  Name: 'Ad',
  Quantity: 'Miktar',
  UnitCode: 'Birim',
  UnitPrice: 'Birim fiyat',
  TotalPrice: 'Tutar',
  VatRate: 'KDV',
  VatExemptionCode: 'KDV istisna kodu',
  StockCode: 'Stok kodu',
  Barcode: 'Barkod',
  Jewellery: 'Kuyum bilgisi',
  SpecialTaxBaseCode: 'Özel matrah kodu',
  GramWeight: 'Gram',
  HasAmount: 'Has bedeli',
  LabourAmount: 'İşçilik',
  Type: 'Tür',
  Amount: 'Tutar',
  InstallmentCount: 'Taksit',
  FirstName: 'Ad',
  LastName: 'Soyad',
  CompanyName: 'Unvan',
  TaxNumber: 'VKN / TCKN',
  TaxOffice: 'Vergi dairesi',
  Phone: 'Telefon',
  Email: 'E-posta',
  Country: 'Ülke',
  City: 'İl',
  District: 'İlçe',
  Address: 'Adres',
  Original: 'İptal edilen işlem',
  ProviderSaleNumber: 'Sağlayıcı satış no',
  ProviderTransactionId: 'Sağlayıcı işlem no',

  // Sağlayıcı zarfı ve link kaydı (Pavo)
  Message: 'Mesaj',
  Data: 'Ayrıntı',
  TargetSerialNo: 'Cihaz seri no',
  PaymentAmount: 'Ödeme tutarı',
  Request: 'Cihaza gönderilen istek',
  Response: 'Cihazın cevabı',
  RequestTime: 'İstek zamanı',
  ResponseTime: 'Cevap zamanı',

  // Cihaza giden satış (Pavo Sale)
  Sale: 'Satış',
  OrderNo: 'Sipariş no',
  AddedSaleItems: 'Satış kalemleri',
  PaymentInformations: 'Ödeme bilgileri',
  CustomerParty: 'Müşteri',
  JewelleryItem: 'Kuyum kalemi',
  TaxGroupCode: 'KDV grubu',
  GramPrice: 'Gram fiyatı',
  LaborCostPerGram: 'Gram başı işçilik',
  SingleLaborCost: 'İşçilik',
  SaleNumber: 'Satış no',
  ItemQuantity: 'Miktar',
  UnitPriceAmount: 'Birim fiyat',
  TotalPriceAmount: 'Tutar',
  StockReference: 'Stok kodu',
  CustomerType: 'Müşteri türü',
  FamilyName: 'Soyad',
  TaxOfficeName: 'Vergi dairesi',
  EMail: 'E-posta',

  // Cihazın cevabı (Pavo terminal yanıtı)
  ErrorCode: 'Hata kodu',
  ErrorMessage: 'Hata mesajı',
  SaleDate: 'Satış tarihi',
  TotalVATAmount: 'Toplam KDV',
  TerminalSerialNo: 'Cihaz seri no',
  DocumentNo: 'Belge no',
  AddedPayments: 'Ödemeler',
  FinancialDocuments: 'Mali belgeler',
  Errors: 'Hatalar',
  AuthorizationCode: 'Onay kodu',
  OnlinePayment: 'Kart işlemi',
  CardNo: 'Kart no',
  AcquirerName: 'Banka',
  ResponseCode: 'Banka cevap kodu',
  ResponseDescription: 'Banka cevabı',
  InvoiceNo: 'Belge no',

  // Deneme (mock) sağlayıcısı
  Status: 'Durum',
  DocumentNumber: 'Belge no',
  ProviderErrorCode: 'Sağlayıcı hata kodu',
};

const PAYMENT_TYPES: Record<number, string> = { 1: 'Kart', 2: 'Nakit', 3: 'Diğer' };

/**
 * Yalnızca belli bir listenin içinde anlamlı alanlar (üst alan adına göre). Sağlayıcının ödeme
 * kalemlerinde asıl ödeme ile karta iade aynı listede gelir; ikisini ayırmak için gösterilir.
 */
const CONTEXT_LABELS: Record<string, Record<string, string>> = {
  AddedPayments: { OperationTypeId: 'İşlem', StatusId: 'Ödeme durumu' },
};
const PAYMENT_OPERATIONS: Record<number, string> = { 1: 'Satış', 2: 'İade' };
/** Pavo Referans Bilgiler › Payment Status. */
const PAYMENT_STATUSES: Record<number, string> = {
  1: 'Ödeme bekleniyor', 2: 'Tamamlandı', 3: 'Başarısız', 4: 'İptal bekleniyor', 5: 'İptal edildi',
  6: 'İptal yapılamadı', 7: 'Terk edildi', 8: 'İptal terk edildi', 9: 'İşlemde', 10: 'Tamamlandı',
  11: 'Başarısız', 12: 'İptal tamamlandı', 13: 'İptal başarısız', 14: 'Terk edildi', 15: 'Ters kayıt bekleniyor',
  16: 'İptal bekleniyor',
};
const CUSTOMER_TYPES: Record<number, string> = { 1: 'Bireysel', 2: 'Kurumsal', 3: 'Kamu' };
const UNIT_CODES: Record<string, string> = { GRM: 'Gram', C62: 'Adet' };
const CURRENCY_CODES: Record<string, string> = { TRY: 'TL', TL: 'TL', USD: 'USD', EUR: 'EUR' };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const MONEY_KEY = /(Amount|Price|Cost)$/;
const QUANTITY_KEY = /(Quantity|Weight)$/;

const moneyFormat = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const quantityFormat = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 3 });

/**
 * POS işleminin sağlayıcıya giden isteğini / gelen cevabını okunur gösterir: yalnızca
 * kullanıcıyı ilgilendiren alanlar Türkçe etiketle, tutarlar Türkçe biçimde, tarihler
 * gün.ay.yıl, satır listeleri tablo olarak. Sağlayıcının metin içine gömdüğü JSON (Pavo
 * link Request/Response, terminal Data) açılarak gösterilir. Tamamı ham veride görünür.
 */
@Component({
  selector: 'app-pos-json-view',
  templateUrl: './pos-json-view.component.html',
})
export class PosJsonViewComponent implements OnChanges {
  @Input() json?: string | null;

  root: JsonSection | null = null;
  /** JSON olarak okunamayan cevap (ör. HTML hata sayfası) olduğu gibi gösterilir. */
  plainText: string | null = null;
  rawJson = '';
  showRaw = false;

  /** Yalnızca LABELS'taki alanlar gösterilir; hiçbiri yoksa (tanınmayan cevap) hepsi gösterilir. */
  private knownOnly = true;

  ngOnChanges(): void {
    this.root = null;
    this.plainText = null;
    this.showRaw = false;
    if (!this.json) return;

    const parsed = tryParseJson(this.json);
    if (parsed === undefined) {
      this.plainText = this.json;
      this.rawJson = this.json;
      return;
    }
    this.rawJson = JSON.stringify(parsed, null, 2);
    if (parsed !== null && typeof parsed === 'object') {
      const build = () => Array.isArray(parsed) ? this.buildSection({ Data: parsed }) : this.buildSection(parsed);
      this.knownOnly = true;
      let section = build();
      if (this.isEmpty(section)) {
        this.knownOnly = false;
        section = build();
      }
      this.root = this.isEmpty(section) ? null : section;
    } else {
      this.plainText = String(parsed);
    }
  }

  private buildSection(obj: Record<string, any>, title?: string, parentKey?: string): JsonSection {
    const section: JsonSection = { title, fields: [], tables: [], sections: [] };

    for (const [key, raw] of Object.entries(obj)) {
      if (this.isHidden(key, parentKey)) continue;
      const value = this.simplify(unwrapJson(raw));
      if (this.isBlank(value)) continue;
      const label = this.label(key, parentKey);

      if (Array.isArray(value)) {
        const items = value.map(item => unwrapJson(item)).filter(item => !this.isBlank(item));
        if (items.every(item => typeof item !== 'object')) {
          section.fields.push({ label, value: items.map(item => this.format(key, item, parentKey)).join(', ') });
        } else if (items.every(item => this.canFlatten(item, key))) {
          section.tables.push(this.buildTable(label, key, items));
        } else {
          items.forEach((item, index) => {
            const child = typeof item === 'object'
              ? this.buildSection(item, `${label} ${index + 1}`, key)
              : { title: `${label} ${index + 1}`, fields: [{ label, value: this.format(key, item, parentKey) }], tables: [], sections: [] };
            if (!this.isEmpty(child)) section.sections.push(child);
          });
        }
      } else if (typeof value === 'object') {
        const child = this.buildSection(value, label, key);
        if (!this.isEmpty(child)) section.sections.push(child);
      } else {
        section.fields.push({ label, value: this.format(key, value, parentKey) });
      }
    }

    // Yalnızca tek alt bölümden ibaret başlık (zarf "Ayrıntı", "Satış") ayrı kademe açmaz.
    if (!section.fields.length && !section.tables.length && section.sections.length === 1) {
      const child = section.sections[0];
      return { ...child, title };
    }
    return section;
  }

  /**
   * Liste tabloya dökülür; satırdaki iç içe nesnelerin alanları (kuyum bilgisi, kart işlemi)
   * ayrı sütun olur. Aynı etiketli alanlar (ödemedeki ve kart işlemindeki onay kodu gibi)
   * tek sütunda birleşir, ilk dolu değer gösterilir.
   */
  private buildTable(title: string, parentKey: string, items: Record<string, any>[]): JsonTable {
    const columns: string[] = [];
    const rows = items.map(item => {
      const cells = new Map<string, string>();
      for (const cell of this.flatten(item, parentKey)) {
        if (!columns.includes(cell.label)) columns.push(cell.label);
        if (!cells.has(cell.label)) cells.set(cell.label, cell.value);
      }
      return cells;
    });
    return {
      title,
      columns,
      rows: rows.map(cells => columns.map(column => cells.get(column) ?? '—')),
    };
  }

  private flatten(item: Record<string, any>, parentKey: string): { label: string; value: string }[] {
    const cells: { label: string; value: string }[] = [];
    for (const [key, raw] of Object.entries(item)) {
      if (this.isHidden(key, parentKey)) continue;
      const value = this.simplify(unwrapJson(raw));
      if (this.isBlank(value)) continue;
      if (Array.isArray(value)) {
        cells.push({ label: this.label(key, parentKey), value: value.map(v => this.format(key, v, parentKey)).join(', ') });
      } else if (typeof value === 'object') {
        cells.push(...this.flatten(value, key));
      } else {
        cells.push({ label: this.label(key, parentKey), value: this.format(key, value, parentKey) });
      }
    }
    return cells;
  }

  private isHidden(key: string, parentKey?: string): boolean {
    return this.knownOnly && !(key in LABELS) && !(parentKey && CONTEXT_LABELS[parentKey]?.[key]);
  }

  private format(key: string, value: any, parentKey?: string): string {
    if (typeof value === 'boolean') return value ? 'Evet' : 'Hayır';

    if (typeof value === 'number') {
      if (key === 'Type' && parentKey === 'Payments') return PAYMENT_TYPES[value] ?? String(value);
      if ((key === 'Type' && parentKey === 'Customer') || key === 'CustomerType') return CUSTOMER_TYPES[value] ?? String(value);
      if (key === 'Status' && !parentKey) return PosTransactionStatusLabels[value] ?? String(value);
      if (parentKey === 'AddedPayments' && key === 'OperationTypeId') return PAYMENT_OPERATIONS[value] ?? String(value);
      if (parentKey === 'AddedPayments' && key === 'StatusId') return PAYMENT_STATUSES[value] ?? String(value);
      if (key === 'VatRate') return `%${quantityFormat.format(value)}`;
      if (MONEY_KEY.test(key)) return moneyFormat.format(value);
      if (QUANTITY_KEY.test(key)) return quantityFormat.format(value);
      return String(value);
    }

    const text = String(value);
    if (key === 'UnitCode') return UNIT_CODES[text] ?? text;
    if (key === 'CurrencyCode') return CURRENCY_CODES[text] ?? text;
    if (ISO_DATE.test(text)) {
      const date = new Date(text);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('tr-TR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
      }
    }
    return text;
  }

  private label(key: string, parentKey?: string): string {
    return (parentKey && CONTEXT_LABELS[parentKey]?.[key]) || LABELS[key] || key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  }

  private isBlank(value: any): boolean {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  /**
   * Adı dışında gösterilecek alanı olmayan tür nesnesi (ör. mali belge türü
   * { Id, Name, Cancellable }) adıyla gösterilir.
   */
  private simplify(value: any): any {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;
    const keys = Object.keys(value).filter(key => !this.isBlank(value[key]));
    return keys.includes('Name') && keys.every(key => key === 'Id' || key === 'Name' || this.isHidden(key))
      ? value.Name
      : value;
  }

  /**
   * Tabloda bir satır olabilir mi: iç içe nesneler sütunlara açılabilir, ama nesne listesi
   * taşıyan satır (satır içinde alt tablo) ayrı bölüm olarak gösterilir.
   */
  private canFlatten(item: any, parentKey: string): boolean {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) return false;
    return Object.entries(item).every(([key, raw]) => {
      if (this.isHidden(key, parentKey)) return true;
      const value = this.simplify(unwrapJson(raw));
      if (this.isBlank(value)) return true;
      if (Array.isArray(value)) return value.every(v => typeof v !== 'object');
      return typeof value !== 'object' || this.canFlatten(value, key);
    });
  }

  private isEmpty(section: JsonSection): boolean {
    return !section.fields.length && !section.tables.length && !section.sections.length;
  }
}
